import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from 'eshava-shared';

import { HelpService } from '@portal/core/help/help.service';
import { Icon } from '@portal/shared/ui/icon/icon';

/**
 * The question-mark next to a field, a column header or a page title.
 *
 * **Renders nothing when there is no text for the key.** Never a placeholder: twenty question marks
 * in one form is noise, and in a dense interface it shows immediately. It also keeps itself honest
 * -- only what has an explanation gets a marker.
 *
 * `tabindex="-1"` on purpose. Twenty help icons would double a form's tab stops; the keyboard route
 * is F1 on the focused field instead, which is what a power user reaches for.
 */
@Component({
  selector: 'portal-help-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslatePipe],
  template: `
    @if (hasHelp()) {
      <button
        type="button"
        class="help-button"
        tabindex="-1"
        [attr.aria-label]="'help.open' | t"
        (click)="open()"
      >
        <portal-icon name="circle-question-regular" size="sm" />
      </button>
    }
  `,
  styles: `
    .help-button {
      display: inline-flex;
      align-items: center;
      padding: var(--space-1);
      color: var(--text-subtle);
      background: none;
      border: 0;
      border-radius: var(--radius-full);
      cursor: pointer;
      transition: color var(--duration) var(--ease);
    }

    .help-button:hover {
      color: var(--accent);
    }
  `
})
export class HelpButton {
  private readonly help = inject(HelpService);

  readonly helpKey = input.required<string>();

  protected readonly hasHelp = computed(() => this.help.hasHelp(this.helpKey()));

  protected open(): void {
    this.help.open(this.helpKey());
  }
}